/**
 * The "plastic kit": shared geometry builders and materials that give every part
 * the same chunky, glossy toy look. All procedural, no model files.
 */
import * as THREE from 'three';

export const STUD = 1;
export const LAYER_H = 0.24;
export const PLATE_H = 0.15;
export const PLATE_W = 0.8;

export const COLORS = {
  board: '#f4f1ff',
  boardRim: '#7c5cff',
  stud: '#d9d2f5',
  chrome: '#e9edf5',
  battery: '#343a5e',
  lamp: '#ffcf33',
  led: '#f7f7fb',
  motor: '#ff7a45',
  fan: '#2fc6ff',
  switch: '#8a5cff',
  button: '#ff4f9a',
  buzzer: '#22c9a0',
  resistor: '#d8c39a',
} as const;

export const WIRE_COLORS: Record<string, string> = {
  blue: '#2f7bff',
  red: '#ff4646',
  green: '#27c46b',
  yellow: '#ffc21f',
  purple: '#a259ff',
  orange: '#ff8a1f',
};
export const WIRE_COLOR_NAMES = Object.keys(WIRE_COLORS);
/** Each wire length gets its own default color, so kids can tell them apart. */
export const WIRE_DEFAULT_COLOR: Record<number, string> = {
  2: 'blue',
  3: 'green',
  4: 'orange',
  5: 'purple',
  6: 'red',
};

const matCache = new Map<string, THREE.Material>();

export function plastic(
  color: string,
  opts: { rough?: number; emissive?: string } = {},
): THREE.MeshStandardMaterial {
  const key = `p:${color}:${opts.rough ?? ''}:${opts.emissive ?? ''}`;
  let m = matCache.get(key) as THREE.MeshStandardMaterial | undefined;
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color,
      roughness: opts.rough ?? 0.32,
      metalness: 0,
      envMapIntensity: 0.9,
    });
    if (opts.emissive) m.emissive = new THREE.Color(opts.emissive);
    matCache.set(key, m);
  }
  return m;
}

export function chrome(): THREE.MeshStandardMaterial {
  let m = matCache.get('chrome') as THREE.MeshStandardMaterial | undefined;
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color: COLORS.chrome,
      roughness: 0.18,
      metalness: 1,
      envMapIntensity: 1.3,
    });
    matCache.set('chrome', m);
  }
  return m;
}

/** A per-instance material (for things that glow or change color on their own). */
export function ownMaterial(color: string, opts: THREE.MeshStandardMaterialParameters = {}) {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.3, metalness: 0, ...opts });
}

const geoCache = new Map<string, THREE.BufferGeometry>();
function cachedGeo(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
  let g = geoCache.get(key);
  if (!g) {
    g = make();
    geoCache.set(key, g);
  }
  return g;
}

function roundedRectShape(w: number, d: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -d / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + d - r);
  s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
  s.lineTo(x + r, y + d);
  s.quadraticCurveTo(x, y + d, x, y + d - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/**
 * A rounded, bevelled slab: length along x, depth along z, height along y (bottom at y = 0).
 */
export function slabGeometry(len: number, depth: number, height: number, radius = 0.2): THREE.BufferGeometry {
  return cachedGeo(`slab:${len}:${depth}:${height}:${radius}`, () => {
    const bevel = Math.min(0.045, height / 3);
    const shape = roundedRectShape(len - bevel * 2, depth - bevel * 2, Math.max(0.01, radius - bevel));
    const g = new THREE.ExtrudeGeometry(shape, {
      depth: height - bevel * 2,
      bevelEnabled: true,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelSegments: 3,
      curveSegments: 10,
    });
    // Extrude goes along +z; stand it up so height is +y.
    g.rotateX(-Math.PI / 2);
    g.translate(0, bevel, 0);
    g.computeVertexNormals();
    return g;
  });
}

export function plateLength(len: number): number {
  return (len - 1) * STUD + PLATE_W;
}

/** The colored base plate every part is mounted on. */
export function plate(len: number, color: string, height = PLATE_H): THREE.Mesh {
  const m = new THREE.Mesh(slabGeometry(plateLength(len), PLATE_W, height, 0.3), plastic(color));
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** A chrome snap button (the electrical contact). */
export function snap(): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(
    cachedGeo('snapBase', () => new THREE.CylinderGeometry(0.2, 0.22, 0.05, 24)),
    chrome(),
  );
  base.position.y = 0.025;
  const nub = new THREE.Mesh(
    cachedGeo('snapNub', () => {
      const geo = new THREE.SphereGeometry(0.12, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2);
      geo.scale(1, 0.75, 1);
      return geo;
    }),
    chrome(),
  );
  nub.position.y = 0.05;
  base.castShadow = nub.castShadow = true;
  g.add(base, nub);
  return g;
}

export function cylinder(rTop: number, rBottom: number, h: number, segs = 24): THREE.BufferGeometry {
  return cachedGeo(
    `cyl:${rTop}:${rBottom}:${h}:${segs}`,
    () => new THREE.CylinderGeometry(rTop, rBottom, h, segs),
  );
}

export function sphere(r: number, w = 24, h = 16): THREE.BufferGeometry {
  return cachedGeo(`sph:${r}:${w}:${h}`, () => new THREE.SphereGeometry(r, w, h));
}

export function box(w: number, h: number, d: number): THREE.BufferGeometry {
  return cachedGeo(`box:${w}:${h}:${d}`, () => new THREE.BoxGeometry(w, h, d));
}

/** A label printed on top of a plate, drawn on a canvas. */
export function decal(
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
  key: string,
  worldW: number,
  worldD: number,
): THREE.Mesh {
  const tex = cachedTexture(`decal:${key}`, () => {
    const c = document.createElement('canvas');
    const scale = 128;
    c.width = Math.round(worldW * scale);
    c.height = Math.round(worldD * scale);
    const ctx = c.getContext('2d')!;
    draw(ctx, c.width, c.height);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 4;
    return t;
  });
  const m = new THREE.Mesh(
    cachedGeo(`plane:${worldW}:${worldD}`, () =>
      new THREE.PlaneGeometry(worldW, worldD).rotateX(-Math.PI / 2),
    ),
    new THREE.MeshStandardMaterial({ map: tex, transparent: true, roughness: 0.4, depthWrite: false }),
  );
  m.renderOrder = 1;
  return m;
}

const texCache = new Map<string, THREE.Texture>();
export function cachedTexture(key: string, make: () => THREE.Texture): THREE.Texture {
  let t = texCache.get(key);
  if (!t) {
    t = make();
    texCache.set(key, t);
  }
  return t;
}

/** Soft radial gradient used for glows and the X-ray particles. */
export function glowTexture(): THREE.Texture {
  return cachedTexture('glow', () => {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.12)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  });
}

export function glowSprite(color: string): THREE.Sprite {
  const s = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowTexture(),
      color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0,
    }),
  );
  s.renderOrder = 10;
  return s;
}

/** Draw a rounded rectangle path. */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
